import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Typography, Button, Box, Alert, CircularProgress, TextField,
  Tabs, Tab, List, ListItem, ListItemAvatar, ListItemText,
  Avatar, Divider, IconButton, ImageList, ImageListItem,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import BarcodeScanner from '../components/BarcodeScanner';
import ModalWrapper from '../components/ModalWrapper';
import GroceryForm from '../forms/GroceryForm';
import {
  Grocery, GroceryFormData, emptyGroceryFormData,
  GrocerySearchResult, GroceryCatalogItem, BarcodeLookupResponse,
} from '../types/Grocery';
import { supportsCameraCapture } from '../utils/camera';
import { haptics } from '../utils/haptics';
import { apiFetch } from '../apiFetch';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

interface GroceryFormModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (grocery: Grocery) => void;
  grocery?: Grocery | null;
}

const GroceryFormModal: React.FC<GroceryFormModalProps> = ({ open, onClose, onSuccess, grocery }) => {
  // Tab state
  const [tabValue, setTabValue] = useState(0);

  // Form state (for manual entry and editing)
  const [formData, setFormData] = useState<GroceryFormData>(emptyGroceryFormData);
  const [errors, setErrors] = useState<Partial<Record<keyof GroceryFormData, string>>>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<GrocerySearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Barcode state
  const [barcodeInput, setBarcodeInput] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [scannerResetTrigger, setScannerResetTrigger] = useState(0);

  // Selected catalog item (from search or barcode)
  const [selectedCatalog, setSelectedCatalog] = useState<GroceryCatalogItem | null>(null);
  const [selectedImageType, setSelectedImageType] = useState<'front' | 'ingredients' | 'nutrition'>('front');

  // Custom image upload
  const [uploadingCustom, setUploadingCustom] = useState(false);
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [useCustomImage, setUseCustomImage] = useState(false);
  const [hasCameraCapture, setHasCameraCapture] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!grocery;
  const apiUrl = process.env.REACT_APP_API_URL;

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      if (grocery) {
        // Editing mode
        setFormData({
          name: grocery.name || '',
          brand: grocery.brand || '',
          category: grocery.category || '',
          description: grocery.description || '',
          barcode: grocery.barcode || '',
          unit: grocery.unit || '',
          packaging_size: grocery.packaging_size || '',
          image: grocery.image || '',
          image_thumbnail: grocery.image_thumbnail || '',
          image_icon: grocery.image_icon || '',
          notes: grocery.notes || '',
        });
        setTabValue(2); // Go to manual tab for editing
      } else {
        // New grocery - start on search tab
        setFormData(emptyGroceryFormData);
        setTabValue(1); // Default to search tab (more desktop-friendly)
      }
      setErrors({});
      setApiError(null);
      setSearchQuery('');
      setSearchResults([]);
      setBarcodeInput('');
      setSelectedCatalog(null);
      setSelectedImageType('front');
      setCustomImagePreview(null);
      setUseCustomImage(false);
      setScannerResetTrigger(0);
    }
  }, [grocery, open]);

  // Check if device supports camera capture (mobile with camera)
  useEffect(() => {
    supportsCameraCapture().then(setHasCameraCapture);
  }, []);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setApiError(null);
  };

  const handleChange = (field: keyof GroceryFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  // === SEARCH FUNCTIONS ===
  const handleSearch = async () => {
    if (searchQuery.trim().length < 2) return;

    setSearchLoading(true);
    setApiError(null);

    try {
      const response = await apiFetch(
        `${apiUrl}/api/groceries/search/?q=${encodeURIComponent(searchQuery.trim())}`
      );
      setSearchResults(response.results || []);
    } catch (err: any) {
      setApiError(err.message || 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleSearchResultClick = async (result: GrocerySearchResult) => {
    setSearchLoading(true);
    setApiError(null);

    try {
      // First, check if user already has this barcode in their groceries
      const existingGroceries = await apiFetch(`${apiUrl}/api/groceries/`);
      const existingGrocery = existingGroceries.find(
        (g: Grocery) => g.barcode === result.barcode
      );

      if (existingGrocery) {
        // User already has this grocery
        haptics.warning();
        setApiError(
          `You already have "${existingGrocery.name}" in your grocery list. ` +
          `Go to your groceries to view or edit it.`
        );
        setSearchLoading(false);
        return;
      }

      // If already in catalog, fetch full details
      // If not, lookup by barcode (which will add to catalog)
      const response: BarcodeLookupResponse = await apiFetch(
        `${apiUrl}/api/groceries/barcode/${result.barcode}/`
      );

      if (response.found && response.data) {
        haptics.medium();
        setSelectedCatalog(response.data);
        setSelectedImageType('front');
        // Pre-fill form with catalog data
        prefillFormFromCatalog(response.data);
      } else {
        haptics.error();
        setApiError('Product not found');
      }
    } catch (err: any) {
      haptics.error();
      setApiError(err.message || 'Failed to fetch product details');
    } finally {
      setSearchLoading(false);
    }
  };

  const getSelectedImageUrls = (catalog: GroceryCatalogItem, type: 'front' | 'ingredients' | 'nutrition') => {
    if (type === 'front') {
      return {
        image_url: catalog.image_front_url || '',
        thumbnail_url: catalog.image_front_thumb_url || '',
        icon_url: catalog.image_front_icon_url || '',
      };
    } else if (type === 'ingredients') {
      return {
        image_url: catalog.image_ingredients_url || '',
        thumbnail_url: catalog.image_ingredients_thumb_url || '',
        icon_url: '',
      };
    } else {
      return {
        image_url: catalog.image_nutrition_url || '',
        thumbnail_url: catalog.image_nutrition_thumb_url || '',
        icon_url: '',
      };
    }
  };

  // === CATALOG SELECTION ===
  const prefillFormFromCatalog = useCallback((catalog: GroceryCatalogItem) => {
    // Clear any validation errors since we're populating the form
    setErrors({});

    // Derive category from OFF categories
    let category = 'Uncategorized';
    if (catalog.categories) {
      const categories = catalog.categories.split(',').map(c => c.trim());

      // First, try to find an English category (en:...)
      let selectedCategory = categories.find(c => c.startsWith('en:'));

      // If no English category, try to find one without a language prefix
      if (!selectedCategory) {
        selectedCategory = categories.find(c => !c.includes(':'));
      }

      // If still nothing, just use the first category
      if (!selectedCategory) {
        selectedCategory = categories[0];
      }

      // Clean up the category
      if (selectedCategory) {
        // Remove language prefix if present
        if (selectedCategory.includes(':')) {
          selectedCategory = selectedCategory.split(':').pop() || 'Uncategorized';
        }
        // Replace hyphens with spaces and capitalize
        category = selectedCategory
          .replace(/-/g, ' ')
          .split(' ')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
          .join(' ')
          .substring(0, 100);
      }
    }

    // Get selected image URLs
    const imageUrls = getSelectedImageUrls(catalog, 'front');

    setFormData({
      name: catalog.name || '',
      brand: catalog.brand || '',
      category: category,
      description: catalog.generic_name || '',
      barcode: catalog.barcode || '',
      unit: '',
      packaging_size: catalog.quantity || '',
      image: imageUrls.image_url,
      image_thumbnail: imageUrls.thumbnail_url,
      image_icon: imageUrls.icon_url,
      notes: '',
    });
  }, []);

  const handleBarcodeLookup = useCallback(async (barcodeOverride?: string) => {
    const barcode = barcodeOverride || barcodeInput.trim();
    if (!barcode) return;

    setBarcodeLoading(true);
    setApiError(null);

    try {
      // First, check if user already has this barcode in their groceries
      const existingGroceries = await apiFetch(`${apiUrl}/api/groceries/`);
      const existingGrocery = existingGroceries.find(
        (g: Grocery) => g.barcode === barcode
      );

      if (existingGrocery) {
        // User already has this grocery
        haptics.warning();
        setApiError(
          `You already have "${existingGrocery.name}" in your grocery list. ` +
          `Go to your groceries to view or edit it.`
        );
        // Clear barcode input and reset scanner so user can scan again
        setBarcodeInput('');
        setScannerResetTrigger(prev => prev + 1);
        setBarcodeLoading(false);
        return;
      }

      // Barcode not in user's list, proceed with lookup
      const response: BarcodeLookupResponse = await apiFetch(
        `${apiUrl}/api/groceries/barcode/${barcode}/`
      );

      if (response.found && response.data) {
        // Medium buzz when lookup succeeds and form is populated
        haptics.medium();
        setApiError(null);
        setSelectedCatalog(response.data);
        setSelectedImageType('front');
        prefillFormFromCatalog(response.data);
      } else {
        // Error buzz when barcode not found
        haptics.error();
        setApiError(`Barcode "${barcode}" not found in database. Try searching by name or add manually.`);
        // Reset scanner so user can try scanning another barcode
        setScannerResetTrigger(prev => prev + 1);
      }
    } catch (err: any) {
      // Error buzz on lookup failure
      haptics.error();
      setApiError(err.message || 'Barcode lookup failed');
      setScannerResetTrigger(prev => prev + 1);
    } finally {
      setBarcodeLoading(false);
    }
  }, [apiUrl, barcodeInput, prefillFormFromCatalog]);

  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleBarcodeLookup();
    }
  };

  const handleImageSelect = (type: 'front' | 'ingredients' | 'nutrition') => {
    if (!selectedCatalog) return;

    setSelectedImageType(type);
    setUseCustomImage(false);
    setCustomImagePreview(null);

    const imageUrls = getSelectedImageUrls(selectedCatalog, type);
    setFormData((prev) => ({
      ...prev,
      image: imageUrls.image_url,
      image_thumbnail: imageUrls.thumbnail_url,
      image_icon: imageUrls.icon_url,
    }));
  };

  // === CUSTOM IMAGE UPLOAD ===
  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      // User cancelled or camera unavailable
      return;
    }

    if (!file.type.startsWith('image/')) {
      setApiError('Please select an image file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setApiError('Image file is too large. Maximum size is 10MB.');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (event) => {
      setCustomImagePreview(event.target?.result as string);
    };
    reader.readAsDataURL(file);

    setUploadingCustom(true);
    setApiError(null);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('image', file);

      const makeUploadRequest = async (token: string) => {
        return fetch(`${apiUrl}/api/groceries/upload-image/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formDataUpload,
        });
      };

      let response = await makeUploadRequest(localStorage.getItem('access_token') || '');

      if (response.status === 401) {
        const refreshToken = localStorage.getItem('refresh_token');
        if (refreshToken) {
          const refreshResponse = await fetch(`${apiUrl}/api/token/refresh/`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh: refreshToken }),
          });

          if (refreshResponse.ok) {
            const tokens = await refreshResponse.json();
            localStorage.setItem('access_token', tokens.access);
            response = await makeUploadRequest(tokens.access);
          } else {
            throw new Error('Session expired. Please log in again.');
          }
        } else {
          throw new Error('Session expired. Please log in again.');
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload image');
      }

      const imageData = await response.json();

      setFormData((prev) => ({
        ...prev,
        image: imageData.image_url,
        image_thumbnail: imageData.thumbnail_url,
        image_icon: imageData.icon_url,
      }));

      setUseCustomImage(true);

    } catch (err: any) {
      setApiError(err.message || 'Failed to upload image.');
      setCustomImagePreview(null);
    } finally {
      setUploadingCustom(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  };

  const handleClearCustomImage = () => {
    setCustomImagePreview(null);
    setUseCustomImage(false);

    // Restore catalog image if available
    if (selectedCatalog) {
      const imageUrls = getSelectedImageUrls(selectedCatalog, selectedImageType);
      setFormData((prev) => ({
        ...prev,
        image: imageUrls.image_url,
        image_thumbnail: imageUrls.thumbnail_url,
        image_icon: imageUrls.icon_url,
      }));
    } else if (!isEditing) {
      // Only clear image URLs if not editing (preserve original when editing)
      setFormData((prev) => ({
        ...prev,
        image: '',
        image_thumbnail: '',
        image_icon: '',
      }));
    }
  };

  // === FORM SUBMISSION ===
  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof GroceryFormData, string>> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Grocery name is required';
    }
    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    setApiError(null);

    // Check for duplicate barcode before submitting (safety net)
    if (!isEditing && formData.barcode.trim()) {
      try {
        const existingGroceries = await apiFetch(`${apiUrl}/api/groceries/`);
        const existingGrocery = existingGroceries.find(
          (g: Grocery) => g.barcode === formData.barcode.trim()
        );

        if (existingGrocery) {
          haptics.error();
          setApiError(
            `You already have "${existingGrocery.name}" with this barcode in your grocery list.`
          );
          setLoading(false);
          return;
        }
      } catch (err) {
        // Continue with submission if check fails
      }
    }

    const payload: Record<string, any> = {
      name: formData.name.trim(),
      brand: formData.brand.trim() || null,
      category: formData.category.trim(),
      description: formData.description.trim() || null,
      barcode: formData.barcode.trim() || null,
      unit: formData.unit.trim() || null,
      packaging_size: formData.packaging_size.trim() || null,
      image: formData.image.trim() || null,
      image_thumbnail: formData.image_thumbnail.trim() || null,
      image_icon: formData.image_icon.trim() || null,
      notes: formData.notes.trim() || null,
      catalog_item_id: selectedCatalog?.id || (isEditing ? grocery?.catalog_item_id : null),
    };

    try {
      let savedGrocery: Grocery;
      if (isEditing && grocery) {
        savedGrocery = await apiFetch(`${apiUrl}/api/groceries/${grocery.id}/`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        savedGrocery = await apiFetch(`${apiUrl}/api/groceries/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      haptics.success();
      onSuccess(savedGrocery);
      onClose();
    } catch (err: any) {
      haptics.error();
      // Handle specific duplicate error from backend
      if (err.message?.includes('duplicate') || err.message?.includes('already exists')) {
        setApiError('You already have a grocery with this barcode in your list.');
      } else {
        setApiError(err.message || 'An error occurred while saving the grocery.');
      }
    } finally {
      setLoading(false);
    }
  };

  // === RENDER HELPERS ===
  const renderCatalogImages = () => {
    if (!selectedCatalog) return null;

    const allImages: { type: 'front' | 'ingredients' | 'nutrition'; url: string | null | undefined }[] = [
      { type: 'front' as const, url: selectedCatalog.image_front_thumb_url },
      { type: 'ingredients' as const, url: selectedCatalog.image_ingredients_thumb_url },
      { type: 'nutrition' as const, url: selectedCatalog.image_nutrition_thumb_url },
    ];
    const images = allImages.filter(img => img.url);

    if (images.length === 0 && !customImagePreview) return null;

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Select Image
        </Typography>
        <ImageList cols={Math.min(images.length + 1, 4)} rowHeight={100} gap={8}>
          {images.map((img) => (
            <ImageListItem
              key={img.type}
              onClick={() => handleImageSelect(img.type)}
              sx={{
                cursor: 'pointer',
                border: selectedImageType === img.type && !useCustomImage ? '3px solid' : '1px solid',
                borderColor: selectedImageType === img.type && !useCustomImage ? 'primary.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <img
                src={img.url!}
                alt={img.type}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {selectedImageType === img.type && !useCustomImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    p: 0.25,
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 18 }} />
                </Box>
              )}
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  bgcolor: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  textAlign: 'center',
                  py: 0.25,
                  textTransform: 'capitalize',
                }}
              >
                {img.type}
              </Typography>
            </ImageListItem>
          ))}

          {/* Custom image preview */}
          {customImagePreview && (
            <ImageListItem
              sx={{
                border: useCustomImage ? '3px solid' : '1px solid',
                borderColor: useCustomImage ? 'primary.main' : 'divider',
                borderRadius: 1,
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <img
                src={customImagePreview}
                alt="Custom"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              {useCustomImage && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    bgcolor: 'primary.main',
                    borderRadius: '50%',
                    p: 0.25,
                  }}
                >
                  <CheckCircleIcon sx={{ color: 'white', fontSize: 18 }} />
                </Box>
              )}
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearCustomImage();
                }}
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: 2,
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 20,
                  height: 20,
                  fontSize: 14,
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                ×
              </IconButton>
            </ImageListItem>
          )}
        </ImageList>

        {/* Upload buttons */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={cameraInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <Button
            size="small"
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingCustom}
          >
            Upload
          </Button>
          {hasCameraCapture && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddAPhotoIcon />}
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploadingCustom}
            >
              Camera
            </Button>
          )}
          {uploadingCustom && <CircularProgress size={20} />}
        </Box>
      </Box>
    );
  };

  // Store barcode lookup function in a ref so scanner callback doesn't cause re-renders
  const handleBarcodeLookupRef = useRef(handleBarcodeLookup);
  useEffect(() => {
    handleBarcodeLookupRef.current = handleBarcodeLookup;
  }, [handleBarcodeLookup]);

  // Memoize the barcode detection handler to prevent scanner restarts
  const handleBarcodeDetected = useCallback((barcode: string) => {
    // Heavy buzz when barcode is detected and populates the field
    haptics.heavy();
    // Clear any previous error when a new barcode is scanned
    setApiError(null);
    setBarcodeInput(barcode);
    // Use setTimeout to break out of the Quagga callback context
    setTimeout(() => {
      handleBarcodeLookupRef.current(barcode);
    }, 100);
  }, []);

  return (
    <ModalWrapper open={open} onClose={onClose}>
      <Typography variant="h6" gutterBottom>
        {isEditing ? 'Edit Grocery' : 'Add Grocery'}
      </Typography>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(null)}>
          {apiError}
        </Alert>
      )}

      {/* Tabs - hide when editing */}
      {!isEditing && (
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 1 }}>
          <Tab icon={<QrCodeScannerIcon />} label="Scan" />
          <Tab icon={<SearchIcon />} label="Search" />
          <Tab icon={<EditIcon />} label="Manual" />
        </Tabs>
      )}

      {/* SCAN TAB */}
      {!isEditing && (
        <TabPanel value={tabValue} index={0}>
          {/* Camera Scanner */}
          {!selectedCatalog && (
            <>
              <BarcodeScanner
                active={tabValue === 0 && open && !selectedCatalog}
                onDetected={handleBarcodeDetected}
                onError={() => {}}
                resetTrigger={scannerResetTrigger}
              />

              <Divider sx={{ my: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  or enter manually
                </Typography>
              </Divider>
            </>
          )}

          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Barcode / PLU"
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyPress={handleBarcodeKeyPress}
              placeholder="e.g., 3017624010701 or 4011"
              InputProps={{
                endAdornment: barcodeLoading ? (
                  <CircularProgress size={20} />
                ) : null,
              }}
            />
            <Button
              variant="contained"
              onClick={() => handleBarcodeLookup()}
              disabled={!barcodeInput.trim() || barcodeLoading}
            >
              Look Up
            </Button>
          </Box>

          {/* Show selected catalog item */}
          {selectedCatalog && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Found: {selectedCatalog.name} {selectedCatalog.brand && `- ${selectedCatalog.brand}`}
                <Button
                  size="small"
                  sx={{ ml: 2 }}
                  onClick={() => {
                    setSelectedCatalog(null);
                    setFormData(emptyGroceryFormData);
                    setBarcodeInput('');
                    setScannerResetTrigger(prev => prev + 1);
                  }}
                >
                  Scan Another
                </Button>
              </Alert>

              {renderCatalogImages()}

              <Divider sx={{ my: 2 }} />

              <GroceryForm formData={formData} onChange={handleChange} errors={errors} compact />
            </Box>
          )}
        </TabPanel>
      )}

      {/* SEARCH TAB */}
      {!isEditing && (
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Search groceries"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              placeholder="e.g., milk, nutella, cheerios..."
              InputProps={{
                endAdornment: searchLoading ? (
                  <CircularProgress size={20} />
                ) : null,
              }}
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={searchQuery.trim().length < 2 || searchLoading}
            >
              <SearchIcon />
            </Button>
          </Box>

          {/* Search results */}
          {searchResults.length > 0 && !selectedCatalog && (
            <List sx={{ maxHeight: 300, overflow: 'auto' }}>
              {searchResults.map((result) => (
                <ListItem
                  key={result.barcode}
                  component="div"
                  onClick={() => handleSearchResultClick(result)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                    borderRadius: 1,
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      src={result.thumbnail_url || undefined}
                      variant="rounded"
                      sx={{ width: 48, height: 48 }}
                    >
                      {result.name.charAt(0)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={result.name}
                    secondary={
                      <>
                        {result.brand && `${result.brand} • `}
                        {result.quantity}
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}

          {searchResults.length === 0 && searchQuery.length >= 2 && !searchLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              No results found. Try a different search or add manually.
            </Typography>
          )}

          {/* Show selected catalog item */}
          {selectedCatalog && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Selected: {selectedCatalog.name} {selectedCatalog.brand && `- ${selectedCatalog.brand}`}
                <Button
                  size="small"
                  sx={{ ml: 2 }}
                  onClick={() => {
                    setSelectedCatalog(null);
                    setFormData(emptyGroceryFormData);
                  }}
                >
                  Clear
                </Button>
              </Alert>

              {renderCatalogImages()}

              <Divider sx={{ my: 2 }} />

              <GroceryForm formData={formData} onChange={handleChange} errors={errors} compact />
            </Box>
          )}
        </TabPanel>
      )}

      {/* MANUAL TAB / EDIT MODE */}
      <TabPanel value={tabValue} index={2}>
        <GroceryForm formData={formData} onChange={handleChange} errors={errors} />

        {/* Image section */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Image {isEditing ? '' : '(optional)'}
          </Typography>

          {/* Show current image when editing (if exists and no custom preview) */}
          {isEditing && formData.image && !customImagePreview && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={formData.image}
                alt="Current"
                style={{
                  maxWidth: 150,
                  maxHeight: 100,
                  borderRadius: 8,
                  border: '2px solid #1976d2',
                }}
              />
              <Typography variant="caption" display="block" color="text.secondary">
                Current image
              </Typography>
            </Box>
          )}

          {/* Show custom image preview */}
          {customImagePreview && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <img
                src={customImagePreview}
                alt="Preview"
                style={{
                  maxWidth: 150,
                  maxHeight: 100,
                  borderRadius: 8,
                  border: '2px solid #1976d2',
                }}
              />
              <IconButton
                size="small"
                onClick={handleClearCustomImage}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  bgcolor: 'error.main',
                  color: 'white',
                  width: 20,
                  height: 20,
                  '&:hover': { bgcolor: 'error.dark' },
                }}
              >
                ×
              </IconButton>
              <Typography variant="caption" display="block" color="text.secondary">
                New image
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <input
              type="file"
              accept="image/*"
              capture="environment"
              ref={cameraInputRef}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileUploadIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingCustom}
            >
              {isEditing && formData.image ? 'Change Photo' : 'Upload Photo'}
            </Button>
            {hasCameraCapture && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddAPhotoIcon />}
                onClick={() => cameraInputRef.current?.click()}
                disabled={uploadingCustom}
              >
                Take Photo
              </Button>
            )}
            {uploadingCustom && <CircularProgress size={20} />}
          </Box>
        </Box>
      </TabPanel>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button variant="outlined" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading || uploadingCustom}
        >
          {loading ? <CircularProgress size={24} /> : isEditing ? 'Save Changes' : 'Add Grocery'}
        </Button>
      </Box>
    </ModalWrapper>
  );
};

export default GroceryFormModal;